import { CHAT_COST, FEE_PERCENTAGE, PRICE_PER_CREDIT, PUBLISH_COST, SIGNUP_CREDITS, SUPPORT_EMAIL, TOP_UP_OPTIONS } from './landingData';
import { fmtIDR } from './format';

import type { LandingLang } from './useLandingLang';

export type FaqLang = LandingLang;

/** One question in both languages. `id` is the #anchor, stable across languages. */
export interface FaqItem {
    id: string;
    q: Record<FaqLang, string>;
    a: Record<FaqLang, string>;
}

export interface FaqGroup {
    id: string;
    title: Record<FaqLang, string>;
    items: FaqItem[];
}

const fee = `${Math.round(FEE_PERCENTAGE * 100)}%`;
const packs = TOP_UP_OPTIONS.join(', ');

/**
 * /faq content. Numbers come from landingData (which mirrors core-api), and the
 * refund / domain / deletion answers paraphrase legalContent — keep them in
 * step when the terms change.
 */
export const FAQ_GROUPS: FaqGroup[] = [
    {
        id: 'getting-started',
        title: { en: 'Getting started', id: 'Memulai' },
        items: [
            {
                id: 'what-is-hyvay',
                q: { en: 'What is HyVay?', id: 'Apa itu HyVay?' },
                a: {
                    en: 'HyVay builds a website from a plain description. You type what you need, for example "a menu page for my coffee shop with a WhatsApp order button", and HyVay writes the site, shows you a live preview and puts it online when you publish.',
                    id: 'HyVay membuat website dari deskripsi biasa. Kamu cukup menulis kebutuhanmu, misalnya "halaman menu kedai kopi dengan tombol pesan via WhatsApp", lalu HyVay membuat situsnya, menampilkan preview langsung, dan menayangkannya saat kamu publish.',
                },
            },
            {
                id: 'need-coding',
                q: { en: 'Do I need to know how to code?', id: 'Apakah saya harus bisa coding?' },
                a: {
                    en: 'No. You change the site by chatting: "make the header green", "add a price list". If you do know code, the Code tab lets you edit the files directly.',
                    id: 'Tidak. Kamu mengubah situs lewat chat: "ganti header jadi hijau", "tambahkan daftar harga". Kalau kamu bisa coding, tab Code memungkinkan kamu mengedit file secara langsung.',
                },
            },
            {
                id: 'how-to-start',
                q: { en: 'How do I start?', id: 'Bagaimana cara mulai?' },
                a: {
                    en: `Create a free account and you get ${SIGNUP_CREDITS} credits. Describe your site on the home page, or pick a ready-made template and change it to fit your business.`,
                    id: `Buat akun gratis dan kamu langsung mendapat ${SIGNUP_CREDITS} credit. Tulis deskripsi situsmu di halaman utama, atau pilih template siap pakai lalu sesuaikan dengan usahamu.`,
                },
            },
            {
                id: 'what-can-i-build',
                q: { en: 'What kind of sites can I build?', id: 'Situs apa saja yang bisa dibuat?' },
                a: {
                    en: 'Business profiles, product catalogs, menus, booking pages, portfolios, invitations and landing pages for a promo or event. For a bigger application, such as one with user accounts or an admin portal, see the custom application package on the Pricing page.',
                    id: 'Profil usaha, katalog produk, menu, halaman booking, portofolio, undangan, dan landing page untuk promo atau acara. Untuk aplikasi yang lebih besar, misalnya dengan akun pengguna atau portal admin, lihat paket aplikasi custom di halaman Pricing.',
                },
            },
            {
                id: 'own-photos',
                q: { en: 'Can I use my own photos and logo?', id: 'Bisakah saya memakai foto dan logo sendiri?' },
                a: {
                    en: 'Yes. Upload them in the Media tab, then mention the file\'s asset id in chat ("use img_7f3a2b as the logo") and HyVay puts it in the right place.',
                    id: 'Bisa. Unggah di tab Media, lalu sebut asset id filenya di chat ("pakai img_7f3a2b sebagai logo"), dan HyVay akan memasangnya di tempat yang tepat.',
                },
            },
        ],
    },
    {
        id: 'pricing',
        title: { en: 'Pricing & payment', id: 'Harga & pembayaran' },
        items: [
            {
                id: 'credits',
                q: { en: 'How does pricing work?', id: 'Bagaimana sistem harganya?' },
                a: {
                    en: `You pay with credits. One credit costs ${fmtIDR(PRICE_PER_CREDIT)}. Each message to the AI costs ${CHAT_COST} credit and each publish costs ${PUBLISH_COST} credits. Previewing, uploading media and viewing your site are free. There is no monthly subscription.`,
                    id: `Kamu membayar dengan credit. Satu credit seharga ${fmtIDR(PRICE_PER_CREDIT)}. Setiap pesan ke AI memakai ${CHAT_COST} credit dan setiap publish memakai ${PUBLISH_COST} credit. Melihat preview, mengunggah media, dan membuka situsmu gratis. Tidak ada langganan bulanan.`,
                },
            },
            {
                id: 'free-trial',
                q: { en: 'Can I try it for free?', id: 'Bisakah dicoba gratis?' },
                a: {
                    en: `Yes. Every new account gets ${SIGNUP_CREDITS} free credits, no payment details needed.`,
                    id: `Bisa. Setiap akun baru mendapat ${SIGNUP_CREDITS} credit gratis, tanpa perlu data pembayaran.`,
                },
            },
            {
                id: 'top-up',
                q: { en: 'How do I top up, and how can I pay?', id: 'Bagaimana cara top up dan membayar?' },
                a: {
                    en: `Choose ${packs} credits or a custom amount. Payment is in rupiah through Midtrans, using any method shown on its checkout page. A ${fee} platform fee is added and shown before you pay.`,
                    id: `Pilih ${packs} credit atau jumlah lain. Pembayaran dalam rupiah lewat Midtrans, dengan metode apa pun yang tampil di halaman pembayarannya. Biaya platform ${fee} ditambahkan dan ditampilkan sebelum kamu membayar.`,
                },
            },
            {
                id: 'expire',
                q: { en: 'Do credits expire?', id: 'Apakah credit bisa hangus?' },
                a: {
                    en: 'No. Credits stay on your account for as long as it is active.',
                    id: 'Tidak. Credit tetap ada di akunmu selama akun masih aktif.',
                },
            },
            {
                id: 'refund',
                q: { en: 'Can I get a refund?', id: 'Apakah bisa refund?' },
                a: {
                    en: `Purchased credits are non-refundable. If you were charged in error, for example a double payment or credits that never arrived, email ${SUPPORT_EMAIL} and we will fix it.`,
                    id: `Credit yang sudah dibeli tidak dapat dikembalikan. Jika terjadi kesalahan tagihan, misalnya pembayaran ganda atau credit tidak masuk, kirim email ke ${SUPPORT_EMAIL} dan kami akan memperbaikinya.`,
                },
            },
        ],
    },
    {
        id: 'publishing',
        title: { en: 'Publishing, domains & analytics', id: 'Publish, domain & analytics' },
        items: [
            {
                id: 'publish',
                q: { en: 'How do I put my site online?', id: 'Bagaimana cara menayangkan situs saya?' },
                a: {
                    en: `Click Publish in Studio and choose a name. Your site goes live at name.hyvay.com with HTTPS included. Publishing costs ${PUBLISH_COST} credits.`,
                    id: `Klik Publish di Studio dan pilih nama. Situsmu tayang di nama.hyvay.com, sudah termasuk HTTPS. Publish memakai ${PUBLISH_COST} credit.`,
                },
            },
            {
                id: 'update',
                q: { en: 'I changed my site. Why is the live version still the old one?', id: 'Saya sudah mengubah situs, kenapa versi live masih yang lama?' },
                a: {
                    en: `Changes in Studio stay in your draft until you publish again, so visitors never see a half-finished edit. Each publish, including updates, costs ${PUBLISH_COST} credits.`,
                    id: `Perubahan di Studio tetap menjadi draft sampai kamu publish lagi, jadi pengunjung tidak pernah melihat hasil edit yang setengah jadi. Setiap publish, termasuk pembaruan, memakai ${PUBLISH_COST} credit.`,
                },
            },
            {
                id: 'custom-domain',
                q: { en: 'Can I use my own domain?', id: 'Bisakah memakai domain sendiri?' },
                a: {
                    en: 'Yes. Connect a domain you already own for free by adding the DNS records HyVay shows you. HTTPS is set up automatically. Buying a new .com or .id domain directly inside HyVay is coming soon.',
                    id: 'Bisa. Hubungkan domain yang sudah kamu miliki secara gratis dengan menambahkan record DNS yang ditampilkan HyVay. HTTPS disiapkan otomatis. Membeli domain .com atau .id baru langsung di HyVay segera hadir.',
                },
            },
            {
                id: 'domain-renewal',
                q: { en: 'Who renews my domain?', id: 'Siapa yang memperpanjang domain saya?' },
                a: {
                    en: 'A domain you connect yourself stays with the registrar you bought it from, so you renew it there. Renew before it expires, because an expired domain can be taken by someone else and your site stops loading on it.',
                    id: 'Domain yang kamu hubungkan sendiri tetap dikelola di registrar tempat kamu membelinya, jadi perpanjangannya juga di sana. Perpanjang sebelum kedaluwarsa, karena domain yang kedaluwarsa bisa diambil orang lain dan situsmu tidak lagi bisa dibuka lewat domain itu.',
                },
            },
            {
                id: 'analytics',
                q: { en: 'Can I see who visits my site?', id: 'Bisakah saya melihat siapa yang mengunjungi situs saya?' },
                a: {
                    en: 'Yes. Every published site has built-in analytics in the Studio Analytics tab: visitors, page views, top pages, traffic sources, location, devices, busiest hours, and clicks on your WhatsApp, phone, Maps and email links. It works on your hyvay.com address and your own domain, needs no setup, uses no cookies, and is free during the beta.',
                    id: 'Bisa. Setiap situs yang sudah publish punya analytics bawaan di tab Analytics Studio: jumlah pengunjung, page view, halaman terpopuler, sumber trafik, lokasi, perangkat, jam teramai, serta klik pada link WhatsApp, telepon, Maps, dan email. Berlaku untuk alamat hyvay.com maupun domain sendiri, tanpa setup, tanpa cookie, dan gratis selama beta.',
                },
            },
        ],
    },
    {
        id: 'data',
        title: { en: 'Data, security & support', id: 'Data, keamanan & bantuan' },
        items: [
            {
                id: 'ownership',
                q: { en: 'Who owns my site, and can I take it elsewhere?', id: 'Siapa pemilik situs saya, dan bisakah dipindahkan?' },
                a: {
                    en: 'Your content stays yours. HyVay only uses it to host and run your site. If you need a copy of your site\'s source code, email support and we will send it to you.',
                    id: 'Kontenmu tetap milikmu. HyVay hanya memakainya untuk meng-hosting dan menjalankan situsmu. Jika kamu butuh salinan source code situsmu, kirim email ke tim bantuan dan kami akan mengirimkannya.',
                },
            },
            {
                id: 'privacy',
                q: { en: 'How is my data protected?', id: 'Bagaimana data saya dilindungi?' },
                a: {
                    en: 'We handle personal data under Indonesia\'s Personal Data Protection Law (UU PDP). Payments are processed by Midtrans, so your card details never reach HyVay. The analytics on your site counts visitors anonymously, without cookies. Our Privacy Policy explains the details.',
                    id: 'Kami mengelola data pribadi sesuai Undang-Undang Pelindungan Data Pribadi (UU PDP). Pembayaran diproses oleh Midtrans, sehingga data kartumu tidak pernah sampai ke HyVay. Analytics di situsmu menghitung pengunjung secara anonim, tanpa cookie. Detailnya ada di Kebijakan Privasi kami.',
                },
            },
            {
                id: 'delete-account',
                q: { en: 'Can I delete my account?', id: 'Bisakah saya menghapus akun?' },
                a: {
                    en: 'Yes. In Profile, choose Delete account and type your email to confirm. This removes your projects, takes your sites offline, deletes your uploaded media and forfeits remaining credits. It cannot be undone.',
                    id: 'Bisa. Di halaman Profile, pilih Delete account lalu ketik emailmu untuk konfirmasi. Tindakan ini menghapus project, menurunkan situsmu, menghapus media yang kamu unggah, dan menghanguskan sisa credit. Tidak bisa dibatalkan.',
                },
            },
            {
                id: 'custom-app',
                q: { en: 'Can HyVay build a custom application for my organisation?', id: 'Bisakah HyVay membuatkan aplikasi custom untuk organisasi saya?' },
                a: {
                    en: 'Yes. Our team builds web, desktop and mobile applications with their own admin portal. Tell us what the app should do and who will use it, and we reply with questions, then a scope and a quote.',
                    id: 'Bisa. Tim kami membangun aplikasi web, desktop, dan mobile lengkap dengan portal admin sendiri. Ceritakan apa yang harus dilakukan aplikasinya dan siapa penggunanya, lalu kami membalas dengan pertanyaan, kemudian ruang lingkup dan penawaran harga.',
                },
            },
            {
                id: 'support',
                q: { en: 'How do I contact support?', id: 'Bagaimana cara menghubungi bantuan?' },
                a: {
                    en: `Email ${SUPPORT_EMAIL}. Include your project name and what happened, and a screenshot if you have one.`,
                    id: `Kirim email ke ${SUPPORT_EMAIL}. Sertakan nama project dan apa yang terjadi, beserta screenshot jika ada.`,
                },
            },
        ],
    },
];
